import {computed, computedFn} from '@api'
import {VipLocalStore} from '@modules'
import {GroupByPagesStore, IGroupByPage, GroupByPageTitleStore, IGroupByPageRow, IPdfGroupBySearchResult_Group} from '@modules/pdf'
import {StoresInstance} from '@stores'

interface GroupPageLengthIndex {
  length: number
  index: number
}

export class GroupByColumnPageStore extends VipLocalStore {
  constructor(stores: StoresInstance, public groupPagesStore: GroupByPagesStore) {super(stores)}

  get size() {
    return this.groupPagesStore.size
  }

  get grouping() {
    return this.groupBy.hierarchyGroups[1]
  }

  get queryLimit() {
    return this.groupPagesStore.queryLimit
  }

  get layout() {
    return this.groupPagesStore.layout
  }

  get groups() {
    return this.groupPagesStore.groups
  }

  get rowsPerPage() {
    return this.groupPagesStore.rowsPerPage
  }

  @computed
  private get hasThreeColumns() {
    const {size} = this
    return size % 3 === 0
  }

  @computed
  private get uniqGroups() {
    const {grouping, groups} = this
    return grouping?.def?.type === 'Boolean' ? [grouping.def?.booleanLabels?.trueLabel ?? 'Yes', grouping.def?.booleanLabels?.falseLabel ?? 'No'] : _.uniq(_.flatMap(groups, g => g.children.map(c => c.title)))
  }

  @computed
  private get columnValues() {
    const {grouping, uniqGroups, hasThreeColumns} = this
    return grouping.applyCustomSortValues(uniqGroups).splice(0, hasThreeColumns ? 3 : 2)
  }

  @computed
  private get divisor() {
    const {hasThreeColumns, columnValues, grouping} = this
    return grouping ? hasThreeColumns ? (columnValues.length < 3 ? columnValues.length : 3) : (columnValues.length < 2 ? columnValues.length : 2) : 0
  }

  @computed
  private get _columns() {
    const {columnValues, divisor} = this
    return _.first(_.chunk(columnValues, divisor))
  }

  get maxLimit() {
    return this.groupPagesStore.maxLimit
  }

  private addNewPage = (pages, group) => {
    const {catalog} = this,
      {title, subtitle} = new GroupByPageTitleStore(catalog.stores, {group})
    pages.push({
      pageIndex: pages.length + 1, columns: [], rows: [
        {
          spec: {title, subtitle, ...group, doc_count: group.items.total.value},
          count: group.items.total,
          items: []
        }
      ], groupName: group.groupName, complete: false, count: 0
    })
    return pages
  }

  private _getGroupCount = (children) => {
    const {_columns} = this
    return children.reduce((acc, child) => {
      if (_columns.includes(child.title)) acc.push({length: child.items.items.length, index: _columns.findIndex(n => n === child.title)})
      return acc
    }, [])
  }

  private _getMaxPagesForGroup = computedFn((counts: Array<GroupPageLengthIndex>): number => !counts.length ? 1 : Math.max(...counts.map((c: GroupPageLengthIndex) => Math.ceil(c.length / ((this.hasThreeColumns ? 3 : Math[!(c.index % 2) ? 'floor' : 'ceil'](this.size / 2)) * this.rowsPerPage)))))

  private _findPage = (page, title, childTitle) => {
    const {divisor, rowsPerPage} = this
    return !page.complete
           && ((page.rows.map(r => r.spec.title).includes(title)
                ? (!(page.columns.map(c => c.rowTitle === title ? c.spec.title : null).includes(childTitle)) && ((page.columns.length % divisor) > 0))
                : (_.sum(_.values(_.groupBy(page.columns, 'rowTitle')).map(g => Math.max(...g.map(v => v.rowSize)))) < rowsPerPage)))
  }

  private groupByColumns = (pages: Array<IGroupByPage>, group) => {
    const {columnValues: uniq, divisor, catalog: {itemCount}, catalog, addNewPage, _getMaxPagesForGroup, rowsPerPage, _columns, maxLimit, size, _findPage, _getGroupCount} = this
    if (uniq.length > divisor) {
      console.log('Groups are more than the columns available, pdf is empty')
      return null
    }
    const {children} = group
    let currentPage = _.first(pages),
      {title, subtitle, key} = new GroupByPageTitleStore(catalog.stores, {group}),
      _groupCount: Array<GroupPageLengthIndex> = _getGroupCount(children),
      lastPage = _.last(pages),
      last = (lastPage?.pageIndex ?? 0) + 1,
      maxPage = _getMaxPagesForGroup(_groupCount),
      max = last + maxPage + (maxPage > 1 && lastPage.complete === false && lastPage.columns.length > 0 ? 1 : 0)
    _columns.forEach((val, index: number) => {
      const child = children.find(c => c.title === val && c.parentStore?.infoCardProps.key === key) ?? {items: {items: [], total: {value: 0}}, title: val, noData: true},
        data: Array<any> = _.cloneDeep(child?.items?.items)
      const a: Array<any> = [],
        columnSize = Math[(!(index % 2) ? 'floor' : 'ceil')](size / divisor)

      if (currentPage) {
        for (let i = last; i <= max; i++) {
          currentPage = pages.find(page => _findPage(page, title, child.title))
          if (!currentPage) {
            pages = addNewPage(pages, group)
            currentPage = _.last(pages)
          }
          const {columns, rows} = currentPage,
            total_rows = _.sum(_.values(_.groupBy(columns, 'rowTitle')).map(g => g.length === divisor ? Math.max(...g.map(v => v.rowSize)) : Math.max(...g.map(v => v.spec.title === child.title ? v.rowSize : 0)))),
            rowsOnPage: number = columns.filter(c => c.spec.title === child.title).length ? total_rows : 0,
            end = a.length + ((rowsPerPage - rowsOnPage) * columnSize),
            items = _.slice(data, a.length, (end > maxLimit ? maxLimit : end)),
            columnItems = (columns.filter(c => c.spec.title === child.title).length > 0) ? [...items] : [{...child, spec: true}, ...items]

          if ((!rows.length || (!rows.find((row: IGroupByPageRow) => row.spec.title === title)) && rows.length < rowsPerPage)) {
            rows.push({
              spec: {title, subtitle, ...group, doc_count: group.items.total.value},
              count: group.items.total,
              items: []
            })
          }

          columns.push({
            spec: child,
            count: group.items.total,
            items: columnItems,
            columnSize,
            rowSize: Math.ceil(items.length / columnSize) || 1,
            rowKey: group.key,
            rowTitle: title
          })
          if (columns.length >= divisor) {
            const after_rows_count = _.sum(_.values(_.groupBy(columns, 'rowTitle')).map(g => Math.max(...g.map(v => v.rowSize))))
            currentPage.complete = (after_rows_count === rowsPerPage && ((columns.length % divisor) === 0) || itemCount === items.length)// ((child.noData && (rowsPerPage === rows.length || maxRow === rowsPerPage)) || ((maxRow === rowsPerPage) || rowsPerPage === rows.length)) && ((columns.length % divisor) === 0)
          }
          a.push(...items)
          currentPage.count += items.length
        }
      }
    })

    return pages.filter(p => p.count > 0)
  }

  @computed get pages() {
    const {groups, pdf, groupByColumns, columnValues} = this

    let pages: IGroupByPage[] = [{pageIndex: 1, rows: [], columns: [], count: 0, complete: false}]
    groups?.forEach((group: IPdfGroupBySearchResult_Group) => {
      columnValues.forEach((u) => {
        const _group = group.children.find(c => c.title === u)
        if (_group) {
          if (!group.limited) {
            group.limited = _group.count
          } else {
            group.limited += _group.count
          }
        }
      })
      pages = group.children.filter(c => columnValues.includes(c.title)).length ? groupByColumns(pages, group) : pages
      pages = pages.map((p, i) => {
        p.pageIndex = i + 1
        return p
      })
      pdf.limitedCount += (group.limited ?? 0)
    })
    return pages.filter(page => page.columns.length > 0)
  }
}


// WEBPACK FOOTER //
// ./src/modules/pdf/templates/vlp/GroupByColumnPageStore.ts